import * as React from "react";
import {
  Create,
  SimpleForm,
  ReferenceInput,
  DateInput,
  AutocompleteInput,
  SelectInput,
  Toolbar,
  SaveButton,
  Button,
  useRedirect,
  NumberInput,
  NumberInputProps,
  useDataProvider,
  required,
  maxValue,
  BooleanInput,
} from "react-admin";
import CancelIcon from "@mui/icons-material/Cancel";

import { useWatch, useFormContext } from "react-hook-form";
import { COUNT_AIRBAG_MAX, COUNT_BINS_MAX, integerOnly } from "../validators";
import { calculateCollecteDate } from "../utils/businessDays";
import { Alert } from "@mui/material";

const priorityChoises = [
  { id: 0, name: "Basse" },
  { id: 1, name: "Médium" },
  { id: 2, name: "Elevé" },
];

export const AirbagQuantitySetter: React.FC<NumberInputProps> = (props) => {
  const dataProvider = useDataProvider();
  const { setValue } = useFormContext();
  const clientId = useWatch({ name: "client" });

  React.useEffect(() => {
    if (!clientId) return;
    dataProvider
      .getList("client-inventory", {
        filter: { client: clientId },
        sort: { field: "createdAt", order: "DESC" },
        pagination: { page: 1, perPage: 1 },
      })
      .then(({ data }) => {
        const lastQty = data[0]?.airbagsCount ?? 0;
        setValue("quantityAirbag", lastQty);
      })
      .catch(() => setValue("quantityAirbag", 0));
  }, [clientId, dataProvider, setValue]);

  return <NumberInput {...props} />;
};

export const BinQuantitySetter: React.FC<NumberInputProps> = (props) => {
  const dataProvider = useDataProvider();
  const { setValue } = useFormContext();
  const clientId = useWatch({ name: "client" });

  React.useEffect(() => {
    if (!clientId) return;
    dataProvider
      .getList("client-inventory", {
        filter: { client: clientId },
        sort: { field: "createdAt", order: "DESC" },
        pagination: { page: 1, perPage: 1 },
      })
      .then(({ data }) => {
        const lastQty = data[0]?.fullBinsCount ?? 0;
        setValue("quantityBin", lastQty);
      })
      .catch(() => setValue("quantityBin", 0));
  }, [clientId, dataProvider, setValue]);

  return <NumberInput {...props} />;
};

const OrderCreateToolbar: React.FC = (props) => {
  const redirect = useRedirect();
  return (
    <Toolbar {...props}>
      <SaveButton />
      <Button
        variant="outlined"
        color="warning"
        label="Annuler"
        size="large"
        onClick={() => redirect("list", "orders")}
        sx={{ ml: 2, pl: 4 }}
      >
        <CancelIcon />
      </Button>
    </Toolbar>
  );
};

export const OrderCreate: React.FC = (props) => {
  return (
    <Create {...props} title="Créer un Bon de Commande" redirect="list">
      <SimpleForm toolbar={<OrderCreateToolbar />}>
        <ReferenceInput
          source="client"
          reference="clients"
          label="Client"
          perPage={50}
        >
          <AutocompleteInput
            optionText={(record) => `${record.internalCode} - ${record.name}`}
            fullWidth
            validate={[required("Le client est requis")]}
            label="Client"
            filterToQuery={(q: any) => ({
              filterTree: {
                operator: "or",
                operands: [
                  {
                    term: {
                      name: "name",
                      filterType: "like",
                      value: q,
                    },
                  },
                  {
                    term: {
                      name: "internalCode",
                      filterType: "like",
                      value: q,
                    },
                  },
                ],
              },
            })}
          />
        </ReferenceInput>

        <ReferenceInput
          source="provider"
          reference="providers"
          label="Prestataire"
          perPage={50}
        >
          <AutocompleteInput
            optionText={(record) => `${record.internalCode} - ${record.name}`}
            fullWidth
            validate={[required("Le prestataire est requis")]}
            label="Prestataire"
            filterToQuery={(q: any) => ({
              filterTree: {
                operator: "or",
                operands: [
                  {
                    term: {
                      name: "name",
                      filterType: "like",
                      value: q,
                    },
                  },
                  {
                    term: {
                      name: "internalCode",
                      filterType: "like",
                      value: q,
                    },
                  },
                ],
              },
            })}
          />
        </ReferenceInput>
        <DateInput
          source="provisionalDate"
          label="Date Prévisionnelle"
          validate={[required("La date prévisionnelle est requise")]}
          defaultValue={calculateCollecteDate()}
        />
        <BinQuantitySetter
          source="quantityBin"
          label="Quantité de caisses pleines à récupérer"
          validate={[
            required("La quantité de caisse est requise"),
            maxValue(COUNT_BINS_MAX, `Maximum ${COUNT_BINS_MAX}`),
            integerOnly("Doit être un nombre décimal"),
          ]}
        />
        <AirbagQuantitySetter
          source="quantityAirbag"
          label="Quantité d'airbag en vrac"
          validate={[
            required("La quantité d'airbag en vrac est requise"),
            maxValue(COUNT_AIRBAG_MAX, `Maximum ${COUNT_AIRBAG_MAX}`),
            integerOnly("Doit être un nombre décimal"),
          ]}
        />
        <SelectInput
          source="priority"
          label="Priorité"
          choices={priorityChoises}
        />
        <BooleanInput
          label="Envoyer le mail au client avec le QR code?"
          source="sendEmailToClient"
          defaultValue={true}
          defaultChecked={true}
        />
        <BooleanInput
          label="Créer le bon de commande sur MKGT?"
          source="createBdcInMkgt"
          defaultValue={true}
          defaultChecked={true}
        />
        <Alert severity="warning">
          Si vous décochez « Créer le bon de commande sur MKGT », le système générera un faux numéro BDC égal à XXXX.
        </Alert>
      </SimpleForm>
    </Create>
  );
};
